// import type { Color } from 'stories/types'

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useMemo } from 'react'

interface PaginationProps {
  /**
   * Total Count
   */
  totalCount: number
  /**
   * Item count per page
   */
  itemCountPerPage?: number
  /**
   * Data
   */
  onNavigate?: (num: number) => void
  /**
   * Page Number
   */
  pageNum?: number
  /**
   * Maximum page count
   */
  maxPageCount?: number
  /**
   * Callback when count per page is updated
   * If provided, shows the count per page select box
   */
  onCountPerPageUpdate?: (count: number) => void
}

/**
 * Primary UI component for user interaction
 */
// @ts-ignore
export const Pagination = ({
  totalCount = 0,
  itemCountPerPage = 10,
  onNavigate = () => {},
  pageNum = 0,
  maxPageCount = 7,
  onCountPerPageUpdate,
}: PaginationProps) => {
  const countPerPageOptions = [10, 20, 50, 100]
  const pageCount = useMemo(() => {
    const needPlus = totalCount % itemCountPerPage != 0
    return Math.floor(totalCount / itemCountPerPage) + (needPlus ? 1 : 0)
  }, [totalCount, itemCountPerPage])

  const renderPageElement = (
    content: string | number | JSX.Element,
    isActive: boolean,
    isDisabled: boolean,
    num: number,
  ) => {
    return (
      <li key={`${num}`}>
        <button
          className={`py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 ${
            isDisabled ? 'bg-gray-100' : 'hover:bg-gray-100 hover:text-gray-700'
          } ${isActive ? 'z-10 text-primary bg-blue-50' : ''}`}
          onClick={() => !isDisabled && !isActive && onNavigate(num)}
          disabled={isDisabled}
        >
          {content}
        </button>
      </li>
    )
  }

  const isShowDots = useMemo(() => pageCount >= maxPageCount, [pageCount])
  // `itemCountPerPage` belongs in both dependency lists: changing the per-page count while
  // staying on page 1 leaves `pageNum` at 0, so without it the caption keeps the previous
  // count and reads "Showing 1 to 10" over fifty rows.
  const from = useMemo(() => itemCountPerPage * pageNum + 1, [pageNum, itemCountPerPage])
  const to = useMemo(
    () => Math.min(itemCountPerPage * (pageNum + 1), totalCount),
    [pageNum, totalCount, itemCountPerPage],
  )

  const renderDotPagination = () => {
    const pageNums = [0]
    // let max = 0
    const maxCnt = maxPageCount - 2
    let from = Math.floor(Math.min(pageNum, pageCount - maxCnt / 2) - maxCnt / 2) + 1
    if (from < 1) from = 1
    let to = Math.min(from + maxCnt, pageCount - 1)
    from = Math.max(1, to - maxCnt)
    for (let i = from; i < to; i++) {
      if (i <= 0 || i >= pageCount - 1) continue
      pageNums.push(i)
    }
    pageNums.push(pageCount - 1)

    return (
      <>
        {pageNums.map((num, index) => {
          if (
            0 < index &&
            index < pageNums.length - 1 &&
            (num != pageNums[index - 1] + 1 || num != pageNums[index + 1] - 1)
          )
            return renderPageElement('...', num == pageNum, false, num)
          return renderPageElement(num + 1, num == pageNum, false, num)
        })}
      </>
    )
  }

  return (
    <div className="w-full p-3 flex flex-wrap justify-end place-items-center text-gray-900">
      <p className="text-[15px] mr-4">
        Showing {from} to {to} of {totalCount} results
      </p>
      <ul className="inline-flex items-center -space-x-px">
        <li>
          <button
            className="block py-2 px-3 ml-0 leading-tight text-gray-500 bg-white rounded-l-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
            onClick={() => pageNum > 0 && onNavigate(pageNum - 1)}
            disabled={pageNum == 0}
          >
            <span className="sr-only">Previous</span>
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
        </li>
        {isShowDots
          ? renderDotPagination()
          : Array.from(Array(pageCount).keys()).map((num: number) =>
              renderPageElement(num + 1, num == pageNum, false, num),
            )}
        <li>
          <button
            className="block py-2 px-3 leading-tight text-gray-500 bg-white rounded-r-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
            onClick={() => pageNum < pageCount - 1 && onNavigate(pageNum + 1)}
            disabled={pageNum == pageCount - 1}
          >
            <span className="sr-only">Next</span>
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </li>
      </ul>
      {onCountPerPageUpdate && (
        <div className="flex items-center ml-4">
          <span className="text-[15px] mr-2">Per page:</span>
          <select
            className="py-2 px-3 text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
            value={itemCountPerPage}
            onChange={(e) => {
              const value = e.target.value
              onCountPerPageUpdate(value === 'all' ? totalCount : Number(value))
            }}
          >
            {countPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
            {/* <option value="0">All</option> */}
          </select>
        </div>
      )}
    </div>
  )
}
